import React, { createContext, useContext, useState, useMemo, ReactNode, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers, type UserLookup } from "@/hooks/use-users";
import { canImpersonate } from "@/lib/constants";

const STORAGE_KEY = "solvrev_impersonation_user_id";

interface ImpersonationContextType {
  /** The DB user currently being viewed (impersonated or self) */
  activeUser: UserLookup | null;
  /** The logged-in user's DB record */
  selfUser: UserLookup | null;
  /** Whether we are currently impersonating someone else */
  isImpersonating: boolean;
  /** Set impersonation target (pass null to clear) */
  setImpersonatedUserId: (userId: string | null) => void;
  /** The impersonated user's ID (null if not impersonating) */
  impersonatedUserId: string | null;
  /** All available users for the dropdown */
  allUsers: UserLookup[];
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  activeUser: null,
  selfUser: null,
  isImpersonating: false,
  setImpersonatedUserId: () => {},
  impersonatedUserId: null,
  allUsers: [],
});

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: allUsers = [] } = useUsers();
  const [impersonatedUserId, setImpersonatedUserIdState] = useState<string | null>(null);

  // Initialize from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setImpersonatedUserIdState(stored);
    }
  }, []);

  // Wrapper function to persist changes to localStorage
  const setImpersonatedUserId = (userId: string | null) => {
    if (userId !== null && !canImpersonate(selfUser?.role)) {
      localStorage.removeItem(STORAGE_KEY);
      setImpersonatedUserIdState(null);
      return;
    }
    setImpersonatedUserIdState(userId);
    if (userId === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, userId);
  };

  // Find the logged-in user's DB record by email
  const selfUser = useMemo((): UserLookup | null => {
    if (!allUsers.length || !user) return null;
    return allUsers.find(u => u.email === user.email) ?? null;
  }, [allUsers, user]);

  const activeUser = useMemo((): UserLookup | null => {
    if (impersonatedUserId && canImpersonate(selfUser?.role)) {
      return allUsers.find(u => u.id === impersonatedUserId) ?? selfUser;
    }
    return selfUser;
  }, [allUsers, selfUser, impersonatedUserId]);

  // Rydd impersonering hvis selfUser ikke lenger er superadmin (f.eks. rolle endret)
  useEffect(() => {
    if (impersonatedUserId && selfUser && !canImpersonate(selfUser.role)) {
      localStorage.removeItem(STORAGE_KEY);
      setImpersonatedUserIdState(null);
    }
  }, [selfUser, impersonatedUserId]);

  const isImpersonating = impersonatedUserId !== null && impersonatedUserId !== selfUser?.id;

  return (
    <ImpersonationContext.Provider
      value={{ activeUser, selfUser, isImpersonating, setImpersonatedUserId, impersonatedUserId, allUsers }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => useContext(ImpersonationContext);
